"use client";

import { useId, useState } from "react";
import { OTPField } from "@base-ui/react/otp-field";

interface OtpInputProps {
  length?: number;
  onComplete: (code: string) => void;
  /** Optional: fires on every value change (partial included) — lets a parent
   * drive an explicit Submit button instead of auto-submitting on complete. */
  onChange?: (code: string) => void;
  disabled?: boolean;
  error?: boolean;
}

export function OtpInput({
  length = 6,
  onComplete,
  onChange,
  disabled,
  error,
}: OtpInputProps) {
  const id = useId();
  const [value, setValue] = useState("");

  const handleValueChange = (next: string) => {
    setValue(next);
    onChange?.(next);
  };

  const boxSize =
    length === 6 ? "size-full h-[48px] lg:size-[58px]" : "w-[72px]";
  const cellClass = `${boxSize} bg-contrast/8 rounded-md lg:p-4 text-[20px] lg:text-[28px] leading-10 text-center font-normal text-heading outline-none transition-all ${
    error
      ? "border border-[#ed6664]"
      : "border border-transparent focus:border-contrast/20"
  }`;

  return (
    <OTPField.Root
      id={id}
      length={length}
      value={value}
      disabled={disabled}
      onValueChange={handleValueChange}
      onValueComplete={onComplete}
      className="flex items-start justify-between gap-1 w-full"
    >
      {Array.from({ length }, (_, index) => (
        <div key={index} className="relative">
          {!value[index] && (
            <span className="absolute inset-0 flex items-center justify-center text-[20px] lg:text-[28px] leading-10 font-normal text-contrast/15 pointer-events-none select-none">
              0
            </span>
          )}
          <OTPField.Input
            className={cellClass}
            aria-label={`Digit ${index + 1} of ${length}`}
          />
        </div>
      ))}
    </OTPField.Root>
  );
}
